import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, IconButton, List, ListItemButton, Paper,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import RouterIcon from '@mui/icons-material/Router';
import ComputerIcon from '@mui/icons-material/Computer';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import accessControlService, { AccessAgent, AccessProfile } from '../../services/accessControlService';

/** Profil çipi için hazır renkler; serbest metin yerine sınırlı palet daha tutarlı görünüyor. */
const PROFILE_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#5d4037', '#455a64'];

type Severity = 'success' | 'error' | 'info' | 'warning';

interface Props {
  agents: AccessAgent[];
  profiles: AccessProfile[];
  profilesLoading: boolean;
  reloadProfiles: () => Promise<void>;
  notify: (message: string, severity?: Severity) => void;
  cm: (key: string, defaultValue: string, options?: Record<string, unknown>) => string;
}

const AccessProfilesTab: React.FC<Props> = ({ agents, profiles, profilesLoading, reloadProfiles, notify, cm }) => {
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AccessProfile | null>(null);
  const [form, setForm] = useState({ name: '', description: '', color: PROFILE_COLORS[0] });
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<AccessProfile | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Kapı seçimi: sunucudan gelen set ile taslak set ayrı tutulur, böylece
  // "Kaydet" yalnızca gerçekten değişiklik varsa etkinleşir.
  const [savedDoorIds, setSavedDoorIds] = useState<number[]>([]);
  const [draftDoorIds, setDraftDoorIds] = useState<number[]>([]);
  const [doorsLoading, setDoorsLoading] = useState(false);
  const [savingDoors, setSavingDoors] = useState(false);
  /** Kaydedilmemiş kapı seçimi varken başka profile geçiş isteği. */
  const [pendingProfileId, setPendingProfileId] = useState<number | null>(null);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === selectedProfileId) || null,
    [profiles, selectedProfileId],
  );

  useEffect(() => {
    if (profiles.length === 0) {
      setSelectedProfileId(null);
      return;
    }
    if (!profiles.some((p) => p.id === selectedProfileId)) {
      setSelectedProfileId(profiles[0].id);
    }
  }, [profiles, selectedProfileId]);

  const loadProfileDoors = useCallback(async (profileId: number) => {
    setDoorsLoading(true);
    try {
      const res = await accessControlService.getProfileDoors(profileId);
      const ids = res.data?.doorIds || [];
      setSavedDoorIds(ids);
      setDraftDoorIds(ids);
    } catch (error: any) {
      setSavedDoorIds([]);
      setDraftDoorIds([]);
      notify(error?.response?.data?.message || cm('accessManagement.errors.profileLoadFailed', 'Groups could not be loaded.'), 'error');
    }
    setDoorsLoading(false);
  }, [notify, cm]);

  useEffect(() => {
    if (selectedProfileId) loadProfileDoors(selectedProfileId);
  }, [selectedProfileId, loadProfileDoors]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', color: PROFILE_COLORS[0] });
    setFormOpen(true);
  };

  const openEdit = (profile: AccessProfile) => {
    setEditing(profile);
    setForm({
      name: profile.name,
      description: profile.description || '',
      color: profile.color || PROFILE_COLORS[0],
    });
    setFormOpen(true);
  };

  const saveProfile = async () => {
    const name = form.name.trim();
    if (!name) return;

    setSaving(true);
    try {
      if (editing) {
        await accessControlService.updateProfile({
          id: editing.id,
          name,
          description: form.description.trim() || null,
          color: form.color || null,
        });
        notify(cm('accessManagement.profiles.updated', 'Group updated.'), 'success');
      } else {
        await accessControlService.createProfile({
          name,
          description: form.description.trim() || null,
          color: form.color || null,
        });
        notify(cm('accessManagement.profiles.created', 'Group created.'), 'success');
      }
      setFormOpen(false);
      await reloadProfiles();
    } catch (error: any) {
      notify(error?.response?.data?.message || cm('accessManagement.errors.profileSaveFailed', 'Group could not be saved.'), 'error');
    }
    setSaving(false);
  };

  const deleteProfile = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await accessControlService.deleteProfile(deleteTarget.id);
      notify(cm('accessManagement.profiles.deleted', 'Group deleted.'), 'success');
      setDeleteTarget(null);
      await reloadProfiles();
    } catch (error: any) {
      notify(error?.response?.data?.message || cm('accessManagement.errors.profileDeleteFailed', 'Group could not be deleted.'), 'error');
    }
    setDeleting(false);
  };

  const toggleDoor = (doorId: number) =>
    setDraftDoorIds((prev) => (prev.includes(doorId) ? prev.filter((id) => id !== doorId) : [...prev, doorId]));

  const togglePanelDoors = (doorIds: number[], checked: boolean) =>
    setDraftDoorIds((prev) => (checked
      ? Array.from(new Set([...prev, ...doorIds]))
      : prev.filter((id) => !doorIds.includes(id))));

  const doorsDirty = useMemo(() => {
    if (savedDoorIds.length !== draftDoorIds.length) return true;
    const saved = new Set(savedDoorIds);
    return draftDoorIds.some((id) => !saved.has(id));
  }, [savedDoorIds, draftDoorIds]);

  const requestProfileSwitch = (profileId: number) => {
    if (profileId === selectedProfileId) return;
    if (doorsDirty) {
      setPendingProfileId(profileId);
      return;
    }
    setSelectedProfileId(profileId);
  };

  const saveDoors = async () => {
    if (!selectedProfileId) return;
    setSavingDoors(true);
    try {
      await accessControlService.setProfileDoors(selectedProfileId, draftDoorIds);
      notify(cm('accessManagement.profiles.doorsSaved', 'Group doors updated.'), 'success');
      setSavedDoorIds(draftDoorIds);
      await reloadProfiles();
    } catch (error: any) {
      notify(error?.response?.data?.message || cm('accessManagement.errors.doorsSaveFailed', 'Group doors could not be saved.'), 'error');
    }
    setSavingDoors(false);
  };

  const hasAnyDoor = agents.some((agent) => agent.panels.some((panel) => panel.doors.length > 0));

  const renderDoorCheckbox = (
    doorId: number,
    doorNumber: number,
    name: string | null,
    direction: string | null,
    isActive: boolean,
  ) => {
    // Yönü atanmamış kapı seçilemez: C parçasındaki karar mantığı giriş/çıkış
    // ayrımına dayanıyor, yönsüz kapı için karar üretilemez.
    const disabled = !direction || !isActive;
    const checked = draftDoorIds.includes(doorId);

    const label = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}>
          {name || `${cm('accessManagement.doorNumber', 'Door')} ${doorNumber}`}
        </Typography>
        {direction === 'entry' && (
          <Chip size="small" variant="outlined" color="success" icon={<LoginIcon />} label={cm('accessManagement.entry', 'Entry')} />
        )}
        {direction === 'exit' && (
          <Chip size="small" variant="outlined" color="warning" icon={<LogoutIcon />} label={cm('accessManagement.exit', 'Exit')} />
        )}
        {!direction && (
          <Chip size="small" variant="outlined" color="warning" icon={<WarningAmberIcon />} label={cm('accessManagement.directionUnset', 'Not set')} />
        )}
        {!isActive && (
          <Chip size="small" variant="outlined" label={cm('accessManagement.inactive', 'Passive')} />
        )}
      </Box>
    );

    return (
      <Tooltip
        key={doorId}
        title={!direction ? cm('accessManagement.profiles.directionRequired', 'Direction is not set, cannot be selected.') : ''}
      >
        <FormControlLabel
          sx={{ display: 'flex', ml: 0 }}
          control={<Checkbox size="small" checked={checked} disabled={disabled} onChange={() => toggleDoor(doorId)} />}
          label={label}
        />
      </Tooltip>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        {/* ─── Profil listesi ─── */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {cm('accessManagement.profiles.title', 'Access Groups')}
              </Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                {cm('accessManagement.profiles.add', 'Add Group')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {cm('accessManagement.profiles.hint', 'A group is a name given to a set of doors.')}
            </Typography>
            <Divider />

            {profilesLoading && profiles.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : profiles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                {cm('accessManagement.profiles.empty', 'No access group yet.')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {profiles.map((profile) => (
                  <ListItemButton
                    key={profile.id}
                    selected={profile.id === selectedProfileId}
                    onClick={() => requestProfileSwitch(profile.id)}
                    sx={{ borderRadius: 1, mb: 0.5, alignItems: 'flex-start' }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: profile.color || 'grey.500', flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight="bold" noWrap>{profile.name}</Typography>
                        {!profile.isActive && (
                          <Chip size="small" variant="outlined" label={cm('accessManagement.inactive', 'Passive')} />
                        )}
                      </Box>
                      {profile.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {profile.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <MeetingRoomIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {profile.doorCount} {cm('accessManagement.profiles.doorCount', 'Doors')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <CreditCardIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {profile.cardCount} {cm('accessManagement.profiles.cardCount', 'Cards')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(profile); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(profile); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* ─── Kapı seçimi ─── */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {cm('accessManagement.profiles.doorSelection', 'Door Selection')}
                  {selectedProfile ? ` — ${selectedProfile.name}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cm('accessManagement.profiles.selectedCount', '{{n}} doors selected', { n: draftDoorIds.length })}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={savingDoors ? <CircularProgress size={16} /> : <SaveIcon />}
                disabled={!selectedProfileId || !doorsDirty || savingDoors}
                onClick={saveDoors}
              >
                {cm('accessManagement.profiles.saveDoors', 'Save Doors')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {cm('accessManagement.profiles.doorSelectionHint', 'Check the doors included in this group.')}
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {!selectedProfileId ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                {cm('accessManagement.profiles.selectProfile', 'Select a group to edit its doors.')}
              </Typography>
            ) : !hasAnyDoor ? (
              <Alert severity="info">
                {cm('accessManagement.profiles.noDoorsAvailable', 'No doors yet. Complete the inventory from the Doors tab first.')}
              </Alert>
            ) : doorsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              agents.map((agent) => (
                <Box key={agent.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <ComputerIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="bold">
                      {agent.name || cm('accessManagement.unnamedAgent', 'Unnamed panel computer')}
                    </Typography>
                  </Box>
                  {agent.panels.map((panel) => {
                    const selectableIds = panel.doors.filter((d) => d.direction && d.isActive).map((d) => d.id);
                    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => draftDoorIds.includes(id));
                    const someSelected = selectableIds.some((id) => draftDoorIds.includes(id));

                    return (
                      <Box key={panel.id} sx={{ pl: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                            disabled={selectableIds.length === 0}
                            onChange={(e) => togglePanelDoors(selectableIds, e.target.checked)}
                          />
                          <RouterIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {panel.name || `${cm('accessManagement.panel', 'Panel')} ${panel.deviceKey.slice(0, 8)}`}
                          </Typography>
                        </Box>
                        <Box sx={{ pl: 4 }}>
                          {panel.doors.map((door) =>
                            renderDoorCheckbox(door.id, door.doorNumber, door.name, door.direction, door.isActive))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Profil ekleme/düzenleme */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing
            ? cm('accessManagement.profiles.edit', 'Edit Group')
            : cm('accessManagement.profiles.add', 'Add Group')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                autoFocus
                size="small"
                label={cm('accessManagement.profiles.name', 'Group name')}
                placeholder={cm('accessManagement.profiles.namePlaceholder', 'e.g. Production')}
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={cm('accessManagement.profiles.description', 'Description')}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {cm('accessManagement.profiles.color', 'Color')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {PROFILE_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setForm((prev) => ({ ...prev, color }))}
                    sx={{
                      width: 28, height: 28, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                      border: form.color === color ? '3px solid' : '1px solid',
                      borderColor: form.color === color ? 'text.primary' : 'divider',
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            {cm('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={saveProfile}
            disabled={saving || !form.name.trim()}
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {cm('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kaydedilmemiş kapı seçimi uyarısı */}
      <Dialog open={pendingProfileId !== null} onClose={() => setPendingProfileId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{cm('accessManagement.profiles.unsavedTitle', 'Unsaved door selection')}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            {cm('accessManagement.profiles.unsavedHint', 'Door changes in this group are not saved yet. If you switch groups they will be lost.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingProfileId(null)}>
            {cm('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setSelectedProfileId(pendingProfileId);
              setPendingProfileId(null);
            }}
          >
            {cm('accessManagement.profiles.discardChanges', 'Discard changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{cm('accessManagement.profiles.deleteTitle', 'Delete group?')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" fontWeight="bold" gutterBottom>{deleteTarget?.name}</Typography>
          <Alert severity={deleteTarget && deleteTarget.cardCount > 0 ? 'warning' : 'info'}>
            {deleteTarget && deleteTarget.cardCount > 0
              ? cm('accessManagement.profiles.deleteWarning', 'Assigned to {{n}} cards.', { n: deleteTarget.cardCount })
              : cm('accessManagement.profiles.deleteWarningNoCards', 'This group is not assigned to any card.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {cm('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteProfile}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} /> : <DeleteIcon />}
          >
            {cm('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccessProfilesTab;
